import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const get = (p, params) => axios.get(`${API}${p}`, { params }).then((r) => r.data);

export const api = {
  summary: () => get("/stats/summary"),
  rawEvents: (params) => get("/raw-events", params),
  rawEvent: (id) => get(`/raw-events/${id}`),
  users: () => get("/admin/users"),
  zapin: {
    status: () => get("/admin/zapin/status"),
    patchSettings: (body) => axios.patch(`${API}/admin/zapin/settings`, body).then((r) => r.data),
    health: () => get("/zapin/health"),
    deliveries: (limit) => get("/admin/zapin/deliveries", { limit }),
    ping: () => axios.post(`${API}/admin/zapin/ping`).then((r) => r.data),
    testRow: (body) => axios.post(`${API}/admin/zapin/test-row`, body).then((r) => r.data),
    runOnce: (body) => axios.post(`${API}/admin/zapin/run-once`, body).then((r) => r.data),
    backfill: (dryRun) => axios.post(`${API}/admin/zapin/backfill`, { dryRun }).then((r) => r.data),
    putMapping: (mapping) => axios.put(`${API}/admin/zapin/mapping`, { mapping }).then((r) => r.data),
  },
  createUser: (body) => axios.post(`${API}/admin/users`, body).then((r) => r.data),
  patchUser: (id, body) => axios.patch(`${API}/admin/users/${id}`, body).then((r) => r.data),
  deleteUser: (id) => axios.delete(`${API}/admin/users/${id}`).then((r) => r.data),
  cameras: () => get("/cameras"),
  camerasStatus: () => get("/cameras/status"),
  camera: (id) => get(`/cameras/${id}`),
  patchCamera: (id, body) => axios.patch(`${API}/cameras/${id}`, body).then((r) => r.data),
  deleteCamera: (id, purge = true) => axios.delete(`${API}/cameras/${id}`, { params: { purgeData: purge } }).then((r) => r.data),
  sites: () => get("/sites"),
  createSite: (body) => axios.post(`${API}/sites`, body).then((r) => r.data),
  updateSite: (id, body) => axios.put(`${API}/sites/${id}`, body).then((r) => r.data),
  deleteSite: (id) => axios.delete(`${API}/sites/${id}`).then((r) => r.data),
  dataIntervals: (params, signal) => axios.get(`${API}/data/intervals`, { params, signal }).then((r) => r.data),
  dataPersons: (params, signal) => axios.get(`${API}/data/person-attributes`, { params, signal }).then((r) => r.data),
  exportUrl: (kind, params) => `${API}/export/${kind}.csv?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)))}`,
  exportEstimate: (kind, params) => get("/export/estimate", { kind, ...params }),
  downloadCsv: (kind, params) => {
    // direct navigation: the auth cookie is sent, nothing is buffered in memory
    const a = Object.assign(document.createElement("a"), { href: api.exportUrl(kind, params), download: "" });
    document.body.appendChild(a); a.click(); a.remove();
  },
  buildSummaries: (params) => axios.post(`${API}/admin/maintenance/build-summaries`, null, { params }).then((r) => r.data),
  summariesStatus: () => get("/admin/maintenance/summaries"),
  settingsInfo: () => get("/settings/info"),
  systemStatus: () => get("/system/status"),
  maintenance: () => get("/admin/maintenance"),
  storage: (allTime = false) => get("/admin/storage", allTime ? { allTime: true } : undefined),
  storagePolicy: () => get("/admin/storage/policy"),
  saveStoragePolicy: (body) => axios.put(`${API}/admin/storage/policy`, body).then((r) => r.data),
  cleanupArchive: (dryRun) => axios.post(`${API}/admin/storage/cleanup`, null, { params: { dryRun } }).then((r) => r.data),
  rebuildIndexes: () => axios.post(`${API}/admin/maintenance/indexes`, {}).then((r) => r.data),
  recomputeDerived: (body) => axios.post(`${API}/admin/maintenance/recompute-derived`, body).then((r) => r.data),
  job: (id) => get(`/admin/maintenance/jobs/${id}`),
  jobs: (limit = 10) => get("/admin/maintenance/jobs", { limit }),
  runBackfill: () => axios.post(`${API}/admin/maintenance/backfill`, {}).then((r) => r.data),
  analytics: {
    overview: (p, signal) => axios.get(`${API}/analytics/overview`, { params: p, signal }).then((r) => r.data),
    timeseries: (p, signal) => axios.get(`${API}/analytics/timeseries`, { params: p, signal }).then((r) => r.data),
    attributes: (p, signal) => axios.get(`${API}/analytics/attributes`, { params: p, signal }).then((r) => r.data),
    sites: (p, signal) => axios.get(`${API}/analytics/sites`, { params: p, signal }).then((r) => r.data),
    heatmap: (p, signal) => axios.get(`${API}/analytics/heatmap`, { params: p, signal }).then((r) => r.data),
    attributesByCamera: (p, signal) => axios.get(`${API}/analytics/attributes-by-camera`, { params: p, signal }).then((r) => r.data),
  },
  configExport: () => get("/admin/config-export"),
  configImport: (config, dryRun) => axios.post(`${API}/admin/config-import`, { config, dryRun }).then((r) => r.data),
  changePassword: (body) => axios.post(`${API}/auth/change-password`, body).then((r) => r.data),
  publicConfig: () => get("/config/public"),
  simulate: (type) => axios.post(`${API}/dev/simulate-push`, { type }).then((r) => r.data),
  clearSimulated: () => axios.delete(`${API}/dev/simulated`).then((r) => r.data),
  clearAll: () => axios.delete(`${API}/dev/all-events`, { data: { confirm: true } }).then((r) => r.data),
  externalRequests: () => get("/diagnostics/external-requests"),
};

export function shortUA(ua, n = 28) {
  if (!ua) return "—";
  return ua.length > n ? ua.slice(0, n) + "…" : ua;
}

export function fmtTz(iso, tz = "Asia/Kuala_Lumpur", opts = {}) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: tz, year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, ...opts,
  }).format(d);
}

export function localDateStr(tz = "Asia/Kuala_Lumpur", d = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const g = (t) => parts.find((p) => p.type === t).value;
  return `${g("year")}-${g("month")}-${g("day")}`;
}

export function dayRangeIso(dateStr, tz = "Asia/Kuala_Lumpur", days = 1) {
  const off = tzOffsetString(tz, new Date(`${dateStr}T12:00:00Z`));
  const from = new Date(`${dateStr}T00:00:00${off}`);
  const to = new Date(from.getTime() + days * 86400000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function tzOffsetString(tz, date) {
  const s = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" }).formatToParts(date).find((p) => p.type === "timeZoneName").value;
  const m = s.match(/GMT([+-]\d{2}:\d{2})?/);
  return m && m[1] ? m[1] : "+00:00";
}

export function fmtKL(iso) {
  return fmtTz(iso, "Asia/Kuala_Lumpur");
}

export function ago(iso) {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(2)} MB`;
}
